import { createViewportObserver } from '@solid-primitives/intersection-observer';
import { createSpring } from '@solid-primitives/spring';
import { createEffect, createSignal, onMount } from 'solid-js';
import type { Accessor } from 'solid-js';

type Props<R> = {
	value: number;
	formatter?: (value: number) => R;
	formatOptions?: Intl.NumberFormatOptions;
	zeroAsDash?: boolean;
	springOpts?: Parameters<typeof createSpring>[1];
};

export function SpringNumber(props: Props<string | number>) {
	const [ref, setRef] = createSignal<HTMLElement>();
	const value = createSpringNumber(() => props.value, ref, props);

	return <span ref={setRef}>{value()}</span>;
}

export function createSpringNumber<R extends string | number>(
	inputValue: Accessor<number>,
	ref: Accessor<HTMLElement | undefined>,
	opts?: Omit<Props<R>, 'value'>,
): Accessor<R> {
	const [value, setValue] = createSpring(0, opts?.springOpts);
	const [add] = createViewportObserver(() => []);

	function trackEl(el: IntersectionObserverEntry) {
		if (el.isIntersecting) {
			void setValue(inputValue);
		}
	}

	createEffect(() => {
		void setValue(inputValue);
	});

	onMount(() => {
		const el = ref();
		if (el) {
			add(el, trackEl);
		}
	});

	function formatter(value: number): R {
		const fmt = new Intl.NumberFormat('en', opts?.formatOptions);
		return (value === 0 && opts?.zeroAsDash ? '-' : opts?.formatter ? opts.formatter(value) : fmt.format(value)) as R;
	}

	return () => formatter(value());
}
